/**
 * Insumos: catalogo y saldo inicial.
 *
 * La creacion pide lo minimo: nombre y unidad. La dimension se deduce de la
 * unidad, asi que capturar un insumo nuevo durante una compra son dos campos.
 */

import { db } from '../schema';
import { enEscritura } from '../tx';
import { nuevoId } from '../ids';
import { exigirTexto, exigirCantidadPositiva, importeOpcional, exigirFecha } from '../validation';
import { ErrorValidacion } from '@/domain/errors';
import { ahoraIso, hoy, type FechaLocal } from '@/domain/dates';
import {
  UNIDAD_BASE,
  aBase,
  dimensionDeUnidad,
  esUnidadConocida,
  type ContextoInsumo,
  type FactorEquivalencia,
  type Dimension,
} from '@/domain/units';
import {
  aplicarEntradaValuada,
  aplicarEntradaSinCosto,
  type EstadoInventario,
} from '@/domain/inventory';
import type { Centavos } from '@/domain/money';
import type { Ingredient, InventoryMovement } from '../types';

/** Contexto de conversion de un insumo. */
export function contextoDe(insumo: Ingredient): ContextoInsumo {
  return {
    nombre: insumo.nombre,
    dimension: insumo.dimension,
    factorEquivalencia: insumo.factorEquivalencia,
  };
}

/** Estado de inventario de un insumo, en la forma que espera el dominio. */
export function estadoDe(insumo: Ingredient): EstadoInventario {
  return {
    stockBase: insumo.stockBase,
    stockValuadoBase: insumo.stockValuadoBase,
    valorCentavos: insumo.valorCentavos,
    ultimoCostoPorBase: insumo.ultimoCostoPorBase,
  };
}

// ---------------------------------------------------------------------------
// Alta y edicion
// ---------------------------------------------------------------------------

export interface DatosNuevoInsumo {
  nombre: string;
  /** Unidad en la que se compra. De aqui se deduce la dimension. */
  unidadCompraPreferida: string;
  factorEquivalencia?: FactorEquivalencia | null;
  /**
   * Fuerza la dimension en la que se MIDE el insumo, cuando difiere de la
   * unidad en que se COMPRA.
   *
   * Caso real: la mantequilla se compra por barra pero se cocina por gramo. Con
   * `dimension: 'masa'` y una equivalencia de 113.25 g por barra, se captura
   * "8 barras", el inventario guarda 906 g, y una receta puede pedir "150 g"
   * (Req. 5.6, 5.7).
   *
   * Exige que la equivalencia declare esa misma unidad de compra: sin eso no
   * habria puente entre las dos medidas.
   */
  dimension?: Dimension;
}

/**
 * Crea un insumo con lo minimo indispensable.
 *
 * La dimension NO se pide normalmente: se deduce de la unidad. Elegir "kg"
 * implica peso; elegir "barra" implica conteo. Un campo menos que llenar en
 * cada captura.
 *
 * Solo se especifica cuando la unidad de compra y la de medida difieren, y en
 * ese caso la equivalencia es obligatoria.
 */
export async function crearInsumo(datos: DatosNuevoInsumo): Promise<Ingredient> {
  const nombre = exigirTexto(datos.nombre, 'nombre', { maximo: 80 });
  const unidad = datos.unidadCompraPreferida;

  if (!esUnidadConocida(unidad)) {
    throw new ErrorValidacion(`La unidad "${unidad}" no existe en el catalogo.`, 'unidad');
  }

  const deducida = dimensionDeUnidad(unidad);
  if (deducida === null) {
    throw new ErrorValidacion(`No se pudo determinar el tipo de medida de "${unidad}".`, 'unidad');
  }

  const dimension = datos.dimension ?? deducida;
  const equivalencia = datos.factorEquivalencia ?? null;

  // Medirse en otra dimension exige la equivalencia: es el unico puente.
  if (dimension !== deducida) {
    if (equivalencia === null || equivalencia.unidadCompra !== unidad) {
      throw new ErrorValidacion(
        `"${nombre}" se compra en ${unidad} pero se mide en ${UNIDAD_BASE[dimension]}. ` +
          `Hace falta declarar cuanto equivale 1 ${unidad} en ${UNIDAD_BASE[dimension]}.`,
        'factorEquivalencia',
      );
    }
    if (equivalencia.cantidadBase <= 0) {
      throw new ErrorValidacion('La equivalencia debe ser mayor que cero.', 'factorEquivalencia');
    }
  }

  const existente = await buscarPorNombreExacto(nombre);
  if (existente !== undefined) {
    throw new ErrorValidacion(`Ya existe un insumo llamado "${nombre}".`, 'nombre');
  }

  const insumo: Ingredient = {
    id: nuevoId('ing'),
    nombre,
    categoria: null,
    dimension,
    unidadBase: UNIDAD_BASE[dimension],
    unidadCompraPreferida: unidad,
    factorEquivalencia: equivalencia,
    stockBase: 0,
    stockValuadoBase: 0,
    valorCentavos: 0,
    ultimoCostoPorBase: null,
    stockMinimoBase: null,
    activo: true,
    creadoEn: ahoraIso(),
  };

  await db.ingredients.add(insumo);
  return insumo;
}

export interface CambiosInsumo {
  nombre?: string;
  unidadCompraPreferida?: string;
  factorEquivalencia?: FactorEquivalencia | null;
}

/**
 * Edita los datos descriptivos de un insumo.
 *
 * NO permite cambiar la dimension: hacerlo invalidaria las cantidades ya
 * guardadas, que estan expresadas en la unidad base de la dimension original.
 */
export async function editarInsumo(id: string, cambios: CambiosInsumo): Promise<void> {
  const insumo = await db.ingredients.get(id);
  if (insumo === undefined) {
    throw new ErrorValidacion(`No se encontro el insumo "${id}".`);
  }

  const parche: Partial<Ingredient> = {};

  if (cambios.nombre !== undefined) {
    const nombre = exigirTexto(cambios.nombre, 'nombre', { maximo: 80 });
    const otro = await buscarPorNombreExacto(nombre);
    if (otro !== undefined && otro.id !== id) {
      throw new ErrorValidacion(`Ya existe un insumo llamado "${nombre}".`, 'nombre');
    }
    parche.nombre = nombre;
  }

  if (cambios.unidadCompraPreferida !== undefined) {
    const unidad = cambios.unidadCompraPreferida;
    if (dimensionDeUnidad(unidad) !== insumo.dimension) {
      throw new ErrorValidacion(
        `La unidad "${unidad}" no corresponde al tipo de medida de "${insumo.nombre}". ` +
          'Cambiar el tipo de medida invalidaria las cantidades ya registradas.',
        'unidad',
      );
    }
    parche.unidadCompraPreferida = unidad;
  }

  if (cambios.factorEquivalencia !== undefined) {
    parche.factorEquivalencia = cambios.factorEquivalencia;
  }

  await db.ingredients.update(id, parche);
}

/** Los insumos referenciados no se borran, se desactivan (Req. 5.9). */
export async function desactivarInsumo(id: string): Promise<void> {
  await db.ingredients.update(id, { activo: false });
}

export async function reactivarInsumo(id: string): Promise<void> {
  await db.ingredients.update(id, { activo: true });
}

// ---------------------------------------------------------------------------
// Consultas
// ---------------------------------------------------------------------------

export async function listarInsumos(soloActivos = true): Promise<Ingredient[]> {
  const todos = await db.ingredients.toArray();
  const filtrados = soloActivos ? todos.filter((i) => i.activo) : todos;
  return filtrados.sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
}

export async function obtenerInsumo(id: string): Promise<Ingredient | undefined> {
  return db.ingredients.get(id);
}

async function buscarPorNombreExacto(nombre: string): Promise<Ingredient | undefined> {
  const normalizado = normalizar(nombre);
  const todos = await db.ingredients.toArray();
  return todos.find((i) => normalizar(i.nombre) === normalizado);
}

/** Sin acentos ni mayusculas, para que "jamon" encuentre "Jamón". */
function normalizar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

/** Busqueda para el selector de la pantalla de compras. */
export async function buscarInsumos(texto: string, soloActivos = true): Promise<Ingredient[]> {
  const insumos = await listarInsumos(soloActivos);
  const aguja = normalizar(texto);
  if (aguja === '') return insumos;

  return insumos
    .filter((i) => normalizar(i.nombre).includes(aguja))
    .sort((a, b) => {
      // Los que empiezan con el texto buscado van primero
      const aEmpieza = normalizar(a.nombre).startsWith(aguja) ? 0 : 1;
      const bEmpieza = normalizar(b.nombre).startsWith(aguja) ? 0 : 1;
      return aEmpieza - bEmpieza || a.nombre.localeCompare(b.nombre, 'es');
    });
}

// ---------------------------------------------------------------------------
// Saldo inicial (Req. 8)
// ---------------------------------------------------------------------------

export interface DatosSaldoInicial {
  ingredientId: string;
  cantidadCaptura: number;
  unidadCaptura: string;
  /** null = no se conoce el costo. Entra al inventario fisico sin valor (Req. 8.4). */
  costoCentavos: Centavos | null;
  fecha?: FechaLocal;
  notas?: string | null;
}

export interface ResultadoSaldoInicial {
  cantidadBase: number;
  conCosto: boolean;
  advertencias: string[];
}

/**
 * Registra lo que ya se tenia antes de empezar a usar la aplicacion.
 *
 * El costo es OPCIONAL a proposito: la usuaria no quiere inventar precios. Sin
 * costo, la cantidad entra al inventario fisico pero queda fuera del acervo
 * valuado, asi que no diluye el costo promedio (Req. 8.4, 8.5).
 */
export async function registrarSaldoInicial(
  datos: DatosSaldoInicial,
): Promise<ResultadoSaldoInicial> {
  const cantidad = exigirCantidadPositiva(datos.cantidadCaptura, 'cantidad');
  const costo = importeOpcional(datos.costoCentavos, 'costo');
  const fecha = exigirFecha(datos.fecha ?? hoy(), 'fecha');

  const insumo = await db.ingredients.get(datos.ingredientId);
  if (insumo === undefined) {
    throw new ErrorValidacion(`No se encontro el insumo "${datos.ingredientId}".`);
  }

  const cantidadBase = aBase(cantidad, datos.unidadCaptura, contextoDe(insumo));

  const resultado =
    costo === null
      ? aplicarEntradaSinCosto(estadoDe(insumo), cantidadBase)
      : aplicarEntradaValuada(estadoDe(insumo), cantidadBase, costo);

  await enEscritura([db.ingredients, db.inventoryMovements], async () => {
    await db.ingredients.update(insumo.id, resultado.estado);
    await db.inventoryMovements.add(
      construirMovimiento({
        ingredientId: insumo.id,
        tipo: 'saldo_inicial',
        fecha,
        deltas: resultado.deltas,
        refTipo: 'saldo_inicial',
        refId: null,
        motivo: datos.notas ?? 'Existencia previa al uso de la aplicacion',
      }),
    );
  });

  return {
    cantidadBase,
    conCosto: costo !== null,
    advertencias: resultado.advertencias,
  };
}

// ---------------------------------------------------------------------------
// Construccion de movimientos
// ---------------------------------------------------------------------------

export interface DatosMovimiento {
  ingredientId: string;
  tipo: InventoryMovement['tipo'];
  fecha: FechaLocal;
  deltas: {
    cantidadBase: number;
    cantidadValuadaBase: number;
    valorCentavos: Centavos;
    afectaValuacion: boolean;
    costoAplicadoCentavos: Centavos | null;
    costoImputado: boolean;
  };
  refTipo: InventoryMovement['refTipo'];
  refId: string | null;
  motivo: string | null;
}

/** Arma un registro del libro a partir de los deltas que calculo el dominio. */
export function construirMovimiento(datos: DatosMovimiento): InventoryMovement {
  return {
    id: nuevoId('mov'),
    ingredientId: datos.ingredientId,
    tipo: datos.tipo,
    fecha: datos.fecha,
    cantidadBase: datos.deltas.cantidadBase,
    cantidadValuadaBase: datos.deltas.cantidadValuadaBase,
    valorCentavos: datos.deltas.valorCentavos,
    afectaValuacion: datos.deltas.afectaValuacion,
    costoAplicadoCentavos: datos.deltas.costoAplicadoCentavos,
    costoImputado: datos.deltas.costoImputado,
    refTipo: datos.refTipo,
    refId: datos.refId,
    motivo: datos.motivo,
    creadoEn: ahoraIso(),
  };
}
